import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Config } from "../config";
import { useThemeProvider } from '../utils/ThemeContext';
import Toast2 from '../components/Toast2';

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { attribs } = useThemeProvider();
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState(null);
  const [toastErrorOpen, setToastErrorOpen] = useState(false);
  const [toastErrorMsg, setToastErrorMsg] = useState('');
  const [toastSuccessOpen, setToastSuccessOpen] = useState(false);
  const [toastSuccessMsg, setToastSuccessMsg] = useState('');
  const [passwordFeedback, setPasswordFeedback] = useState({
    isValid: false,
    errors: [],
    strength: 'weak' // 'weak', 'medium', 'strong'
  });
  
  useEffect(() => {
    // Verify the token is valid
    const verifyToken = async () => {
      try {
        const startTime = Date.now();
        await axios.get(`${Config.APIBaseURL}/auth/verify-reset-token/${token}`);
        
        // Apply minimum processing time
        const elapsedTime = Date.now() - startTime;
        const minProcessingTime = 1000;
        const additionalDelay = Math.max(0, minProcessingTime - elapsedTime);
        
        if (additionalDelay > 0) {
          await new Promise(resolve => setTimeout(resolve, additionalDelay));
        }
        
        setIsTokenValid(true);
      } catch (error) {
        setIsTokenValid(false);
        setToastErrorOpen(true);
        setToastErrorMsg('Invalid or expired password reset link. Please request a new one.');
      }
    };
    
    if (token) {
      verifyToken();
    } else {
      setIsTokenValid(false);
      setToastErrorOpen(true);
      setToastErrorMsg('Missing reset token. Please request a new password reset link.');
    }
  }, [token]);
  
  useEffect(() => {
    if (formData.password) {
      const errors = validatePassword(formData.password);
      
      // Calculate password strength
      let strength = 'weak';
      if (errors.length === 0) {
        strength = 'strong';
      } else if (errors.length <= 2) {
        strength = 'medium';
      }
      
      setPasswordFeedback({
        isValid: errors.length === 0,
        errors,
        strength
      });
    } else {
      setPasswordFeedback({
        isValid: false,
        errors: [],
        strength: 'weak'
      });
    }
  }, [formData.password]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Complete password policy validation
    const passwordErrors = validatePassword(formData.password);
    
    if (passwordErrors.length > 0) {
      setToastErrorOpen(true);
      setToastErrorMsg(passwordErrors[0]); // Show the first error
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setToastErrorOpen(true);
      setToastErrorMsg('Passwords do not match');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const startTime = Date.now();
      await axios.post(
        `${Config.APIBaseURL}/auth/reset-password`,
        {
          token,
          password: formData.password
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Apply minimum processing time
      const elapsedTime = Date.now() - startTime;
      const minProcessingTime = 1000;
      const additionalDelay = Math.max(0, minProcessingTime - elapsedTime);
      
      if (additionalDelay > 0) {
        await new Promise(resolve => setTimeout(resolve, additionalDelay));
      }
      
      setToastSuccessOpen(true);
      setToastSuccessMsg('Your password has been successfully reset.');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error) {
      setToastErrorOpen(true);
      setToastErrorMsg('An error occurred. Please try again or request a new reset link.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add this function to validate password against the complete policy
  const validatePassword = (password) => {
    const errors = [];
    
    // Check minimum length
    if (password.length < 12) {
      errors.push('Password must be at least 12 characters long');
    }
    
    // Check for uppercase letters
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must include at least one uppercase letter');
    }
    
    // Check for lowercase letters
    if (!/[a-z]/.test(password)) {
      errors.push('Password must include at least one lowercase letter');
    }
    
    // Check for digits
    if (!/\d/.test(password)) {
      errors.push('Password must include at least one number');
    }
    
    // Check for special characters
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors.push('Password must include at least one special character');
    }
    
    // Check for maximum repeated characters
    const repeatedCharsRegex = /(.)\1{4,}/;
    if (repeatedCharsRegex.test(password)) {
      errors.push('Password cannot contain more than 4 repeated characters in a row');
    }
    
    return errors;
  };

  return (
    <div className={`min-h-screen bg-gray-${attribs.bg} flex flex-col justify-center py-12 sm:px-6 lg:px-8`}>
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex flex-col items-center justify-center">
          <img
            className="max-w-xs mx-auto"
            src={attribs.logo}
            alt="Your Company"
          />
          <p className={`mt-2 text-center text-sm text-gray-${attribs.txtgray} w-full`}>
            Create a new password for your account
          </p>
        </div>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className={`bg-${attribs.cardBg} py-8 px-4 shadow sm:rounded-lg sm:px-10`}>
          {isTokenValid === false ? (
            <div className="text-center">
              {toastErrorOpen && (
                <h2 className="text-center text-sm text-gray-600 mb-2">
                  {<Toast2 type="error" open={toastErrorOpen} setOpen={setToastErrorOpen}>{toastErrorMsg}</Toast2>}
                </h2>
              )}
              <div className="mt-4">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-indigo-500 hover:text-indigo-400"
                >
                  Request a new reset link
                </Link>
              </div>
            </div>
          ) : isTokenValid === true ? (
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                {toastErrorOpen && (
                  <h2 className="text-center text-sm text-gray-600 mb-2">
                    {<Toast2 type="error" open={toastErrorOpen} setOpen={setToastErrorOpen}>{toastErrorMsg}</Toast2>}
                  </h2>
                )}
                {toastSuccessOpen && (
                  <h2 className="text-center text-sm text-gray-600 mb-2">
                    {<Toast2 type="success" open={toastSuccessOpen} setOpen={setToastSuccessOpen}>{toastSuccessMsg}</Toast2>}
                  </h2>
                )}
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="New Password"
                    className={`appearance-none block w-full px-3 py-2 border border-gray-${attribs.borderform} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-500`}
                  />
                </div>
                {formData.password && (
                  <div className="mt-2">
                    <div className="flex mb-1">
                      <div className={`h-1 flex-1 rounded-l-full ${
                        passwordFeedback.strength === 'weak' ? 'bg-red-500' : 'bg-gray-200'
                      }`}></div>
                      <div className={`h-1 flex-1 ${
                        passwordFeedback.strength === 'medium' || passwordFeedback.strength === 'strong' ? 'bg-yellow-500' : 'bg-gray-200'
                      }`}></div>
                      <div className={`h-1 flex-1 rounded-r-full ${
                        passwordFeedback.strength === 'strong' ? 'bg-green-500' : 'bg-gray-200'
                      }`}></div>
                    </div>
                    <ul className="text-xs text-gray-500 space-y-1 mt-1">
                      {passwordFeedback.errors.map((error, index) => (
                        <li key={index} className="flex items-center">
                          <svg className="w-3 h-3 mr-1 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          {error}
                        </li>
                      ))}
                      {passwordFeedback.isValid && (
                        <li className="flex items-center">
                          <svg className="w-3 h-3 mr-1 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Password meets all requirements
                        </li>
                      )}
                    </ul>
                  </div>
                )}
                <div className="mt-4">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm New Password"
                    className={`appearance-none block w-full px-3 py-2 border border-gray-${attribs.borderform} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-500`}
                  />
                </div>
              </div>
              <div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                    isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {isSubmitting ? 'Resetting Password...' : 'Reset Password'}
                </button>
              </div>
            </form>
          ) : (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          )}

          <div className="mt-6">
            <div className="mt-6 text-center">
              <Link 
                to="/login" 
                className="text-sm text-indigo-500 hover:text-indigo-400"
              >
                Back to login
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}